/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { QueryClient, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { chunk, isEmpty } from 'lodash';
import { isDefined } from '~shared/helpers/types';
import { createQueryHook, StaleTime } from '~shared/queries/common';
import {
  createBulkWorkItems,
  createJiraProjectBinding,
  createJiraWorkItem,
  deleteJiraBinding,
  deleteJiraProjectBinding,
  getAtlassianApplicationConfiguration,
  getJiraBulkWorkItems,
  getJiraLinkedIssuesCount,
  getJiraOrganizationBinding,
  getJiraProcessingWorkItems,
  getJiraProjectBinding,
  getJiraProjects,
  getJiraProjectSummary,
  getJiraUserActions,
  getJiraWorkItem,
  getJiraWorkTypes,
  patchJiraBinding,
  patchJiraOrgBindingProperties,
  patchJiraSelectedWorkTypes,
  postAtlassianApplicationConfiguration,
  postJiraBinding,
  unlinkSonarIssueFromJiraWorkItem,
  updateJiraProjectBinding,
} from '../api/jira';
import {
  JiraBulkWorkItemQueryResult,
  JiraBulkWorkItemsQueryParams,
  JiraOrganizationBinding,
  JiraProjectBinding,
  JiraWorkItemQueryParams,
  JiraWorkItemResourceType,
  JiraWorkType,
  JiraWorkTypeQueryParams,
} from '../types';

const JIRA_WORK_TYPES = 'jira-work-types';
const JIRA_USER_ACTIONS = 'jira-user-actions';
const JIRA_LINKED_ISSUES_COUNT = 'jira-linked-issues-count';
const JIRA_BULK_WORK_ITEMS = 'jira-bulk-work-items';

/*
 * Query keys utils
 */

const organizationJiraBindingsQueryKeys = {
  byOrganizationKey: (sonarOrganizationUuid: string) =>
    ['organization-jira-binding', sonarOrganizationUuid] as const,
};

const atlassianApplicationConfigurationQueryKey = 'atlassian-application-configuration';

const jiraProjectQueryKeys = {
  byKey: (sonarProjectId: string) => ['jiraProjectBinding', sonarProjectId] as const,
  bySummary: (sonarProjectId: string) => ['jiraProjectSummary', sonarProjectId] as const,
  byOrganizationUuid: (sonarOrganizationUuid: string) =>
    ['jiraProjects', sonarOrganizationUuid] as const,
};

const jiraWorkItemQueryKeys = {
  byKey: (sonarProjectId: string, resourceId: string, resourceType: JiraWorkItemResourceType) =>
    ['jira-work-item-key', sonarProjectId, resourceId, resourceType] as const,
};

/*
 * Organization Jira binding
 */

export const useJiraOrganizationBindingQuery = createQueryHook((sonarOrganizationUuid: string) => {
  return queryOptions({
    queryKey: organizationJiraBindingsQueryKeys.byOrganizationKey(sonarOrganizationUuid),
    queryFn: () => getJiraOrganizationBinding(sonarOrganizationUuid),
    staleTime: StaleTime.LONG,
  });
});

export function usePostJiraBindingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postJiraBinding,
    onSuccess: (maybeBinding) => {
      onSucessfulOrganizationBindingCreation(queryClient, maybeBinding.binding);
    },
  });
}

export function usePatchJiraBindingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchJiraBinding,
    onSuccess: (binding) => {
      onSucessfulOrganizationBindingCreation(queryClient, binding);
    },
  });
}

/*
 * Query to patch Jira binding properties, like isTokenShared
 */
export function usePatchJiraBindingPropertiesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchJiraOrgBindingProperties,
    onSuccess: (binding) => {
      onSucessfulOrganizationBindingCreation(queryClient, binding);
    },
  });
}

function onSucessfulOrganizationBindingCreation(
  queryClient: QueryClient,
  binding?: JiraOrganizationBinding,
) {
  if (binding) {
    queryClient.setQueryData(
      organizationJiraBindingsQueryKeys.byOrganizationKey(binding.sonarOrganizationUuid),
      binding,
    );
  }
}

export function useDeleteJiraBindingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteJiraBinding,
    onSuccess: (_, sonarOrganizationUuid) => {
      queryClient.invalidateQueries({
        queryKey: organizationJiraBindingsQueryKeys.byOrganizationKey(sonarOrganizationUuid),
      });
    },
  });
}

export const useGetAtlassianApplicationConfigurationQuery = createQueryHook(() => {
  return queryOptions({
    queryKey: [atlassianApplicationConfigurationQueryKey],
    queryFn: getAtlassianApplicationConfiguration,
    staleTime: StaleTime.LONG,
  });
});

export function usePostAtlassianApplicationConfigurationMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postAtlassianApplicationConfiguration,
    onSuccess: (data) => {
      queryClient.setQueryData([atlassianApplicationConfigurationQueryKey], data);
    },
  });
}

/*
 * Project Jira binding
 */

export const useJiraProjectSummaryQuery = createQueryHook((projectId: string) => {
  return queryOptions({
    queryKey: jiraProjectQueryKeys.bySummary(projectId),
    queryFn: () => getJiraProjectSummary(projectId),
    enabled: !!projectId,
    staleTime: StaleTime.MEDIUM,
  });
});

/**
 * Get the list of Jira projects that can be bound given a Sonar organization UUIDv4.
 * @param sonarOrganizationUuid
 */
export const useJiraProjectsQuery = createQueryHook((sonarOrganizationUuid: string) => {
  return queryOptions({
    queryKey: jiraProjectQueryKeys.byOrganizationUuid(sonarOrganizationUuid),
    queryFn: () => getJiraProjects(sonarOrganizationUuid),
    enabled: isDefined(sonarOrganizationUuid),
    staleTime: StaleTime.LONG,
  });
});

export const useJiraProjectBindingQuery = createQueryHook((sonarProjectId: string) => {
  return queryOptions({
    queryKey: jiraProjectQueryKeys.byKey(sonarProjectId),
    queryFn: () => getJiraProjectBinding(sonarProjectId),
    enabled: !!sonarProjectId,
    staleTime: StaleTime.NEVER,
  });
});

export function useCreateJiraProjectBindingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jiraProjectBinding: Omit<JiraProjectBinding, 'id'>) =>
      createJiraProjectBinding(jiraProjectBinding),
    onSuccess: getSuccessHandlerProjectBinding(queryClient),
  });
}
export function useUpdateJiraProjectBindingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      jiraProjectBinding: Pick<JiraProjectBinding, 'sonarProjectId' | 'jiraProjectKey'>,
    ) => updateJiraProjectBinding(jiraProjectBinding),
    onSuccess: getSuccessHandlerProjectBinding(queryClient),
  });
}

function getSuccessHandlerProjectBinding(queryClient: QueryClient) {
  return function (newProjectBinding: JiraProjectBinding) {
    const queryKey = jiraProjectQueryKeys.byKey(newProjectBinding.sonarProjectId);
    queryClient.setQueryData(queryKey, newProjectBinding);
    // Silent invalidation to avoid refetching the data
    queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
  };
}

const EMPTY_JIRA_PROJECT_BINDING = {
  id: '00000000-0000-0000-0000-000000000000',
  jiraOrganizationId: '',
  jiraProjectKey: '',
  sonarProjectId: '',
};

export function useDeleteJiraProjectBindingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sonarProjectId: string) => deleteJiraProjectBinding(sonarProjectId),
    onSuccess: (_, sonarProjectId) => {
      const queryKey = jiraProjectQueryKeys.byKey(sonarProjectId);
      // Set binding to empty to avoid refetching
      queryClient.setQueryData(queryKey, EMPTY_JIRA_PROJECT_BINDING);
      queryClient.invalidateQueries({ queryKey, refetchType: 'none' });

      queryClient.removeQueries({
        queryKey: jiraProjectQueryKeys.bySummary(sonarProjectId),
      });

      // Remove all queries related to Jira work items for this project
      queryClient.removeQueries({
        queryKey: ['jira-work-item-key', sonarProjectId],
      });
    },
  });
}

/*
 * Returns work types whose fields are optionally included.
 */

export const useJiraWorkTypesQuery = createQueryHook((params: JiraWorkTypeQueryParams) => {
  const client = useQueryClient();
  return queryOptions({
    queryKey: [JIRA_WORK_TYPES, params.sonarProjectId, params.jiraProjectKey, params.includeFields],
    queryFn: async () => {
      const result = await getJiraWorkTypes(params);

      // set cache work types without fields to avoid doing extra call for includeFlags: false
      if (params.includeFields) {
        client.setQueryData(
          [JIRA_WORK_TYPES, params.sonarProjectId, params.jiraProjectKey, false],
          result.map((workType) => ({
            ...workType,
            fields: [],
          })),
        );
      }

      return result;
    },
    staleTime: StaleTime.LONG,
    enabled: isDefined(params.sonarProjectId),
  });
});

/*
 * WORK ITEM QUERIES & MUTATIONS
 */

export function useCreateJiraWorkItemMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createJiraWorkItem,
    onSuccess: (newJiraWorkItem, jiraWorkItemPayload) => {
      if (!jiraWorkItemPayload.resourceId) {
        return;
      }

      queryClient.setQueryData(
        jiraWorkItemQueryKeys.byKey(
          jiraWorkItemPayload.sonarProjectId,
          jiraWorkItemPayload.resourceId,
          jiraWorkItemPayload.resourceType,
        ),
        { ...newJiraWorkItem, sonarIssueKey: jiraWorkItemPayload.resourceId },
      );

      // Invalidate bulk work item queries
      queryClient.invalidateQueries({
        queryKey: [JIRA_BULK_WORK_ITEMS, jiraWorkItemPayload.sonarProjectId],
      });
    },
  });
}

export function useUnlinkSonarIssueFromJiraWorkItemMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sonarProjectId,
      resourceId,
      resourceType,
    }: {
      resourceId: string;
      resourceType: JiraWorkItemResourceType;
      sonarProjectId: string;
    }) => unlinkSonarIssueFromJiraWorkItem(sonarProjectId, resourceId, resourceType),
    onSuccess: (_, { sonarProjectId, resourceId, resourceType }) => {
      queryClient.setQueryData(
        jiraWorkItemQueryKeys.byKey(sonarProjectId, resourceId, resourceType),
        {},
      );

      // Invalidate bulk work item queries
      queryClient.invalidateQueries({
        queryKey: [JIRA_BULK_WORK_ITEMS, sonarProjectId],
      });
    },
  });
}

export function useCreateBulkWorkItemsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['create-bulk-jira-work-items'],
    mutationFn: createBulkWorkItems,
    onSuccess: (jiraLinkedSonarIssues, jiraBulkWorkItemPayload) => {
      // Map each linked sonar issue to its corresponding work item
      jiraLinkedSonarIssues.forEach((linkedSonarIssue) => {
        queryClient.setQueryData(
          jiraWorkItemQueryKeys.byKey(
            jiraBulkWorkItemPayload.sonarProjectId,
            linkedSonarIssue.resourceId,
            linkedSonarIssue.resourceType,
          ),
          { ...linkedSonarIssue.jiraWorkItem, sonarIssueKey: linkedSonarIssue.resourceId },
        );
      });

      // Invalidate linked issues count for the project
      queryClient.invalidateQueries({
        queryKey: [JIRA_LINKED_ISSUES_COUNT, jiraBulkWorkItemPayload.sonarProjectId],
      });
      // Invalidate jira work items for the project
      queryClient.invalidateQueries({
        queryKey: [JIRA_BULK_WORK_ITEMS, jiraBulkWorkItemPayload.sonarProjectId],
      });
    },
  });
}

export const useGetJiraWorkItemQuery = createQueryHook(
  ({ resourceId, resourceType, sonarProjectId }: JiraWorkItemQueryParams) => {
    return queryOptions({
      queryKey: jiraWorkItemQueryKeys.byKey(sonarProjectId, resourceId, resourceType),
      queryFn: async () => {
        return getJiraWorkItem(sonarProjectId, resourceId, resourceType);
      },
      enabled: isDefined(sonarProjectId) && isDefined(resourceId) && isDefined(resourceType),
      staleTime: StaleTime.NEVER,
    });
  },
);

const ISSUES_PER_CHUNK = 200;
export const useGetJiraBulkWorkItemsQuery = createQueryHook(
  ({ sonarIssueKeys, sonarProjectId, resourceType }: JiraBulkWorkItemsQueryParams) => {
    const queryClient = useQueryClient();
    return queryOptions({
      queryKey: [JIRA_BULK_WORK_ITEMS, sonarProjectId, sonarIssueKeys, resourceType],
      queryFn: async () => {
        // From sonarIssueKeys, get cached work items and filter out those that are already cached
        const { filteredIssueKeys, cachedJiraWorkItems } = sonarIssueKeys.reduce<{
          cachedJiraWorkItems: JiraBulkWorkItemQueryResult[];
          filteredIssueKeys: string[];
        }>(
          (data, issueKey) => {
            const workItem = queryClient.getQueryData<JiraBulkWorkItemQueryResult>(
              jiraWorkItemQueryKeys.byKey(sonarProjectId, issueKey, resourceType),
            );

            if (workItem) {
              data.cachedJiraWorkItems.push(workItem);
            } else {
              data.filteredIssueKeys.push(issueKey);
            }

            return data;
          },
          { filteredIssueKeys: [], cachedJiraWorkItems: [] },
        );

        const cachedNotEmptyWorkItems = cachedJiraWorkItems.filter(
          (workItem) => !isEmpty(workItem),
        );

        // if all issues are cached, return them and do not make an API call
        if (filteredIssueKeys.length === 0) {
          return cachedNotEmptyWorkItems;
        }

        // Make api calls in chunks to avoid hitting the API limits
        // for those issues that are not cached
        const apiChunks = chunk(filteredIssueKeys, ISSUES_PER_CHUNK);

        const result = await Promise.all(
          apiChunks.map((chunkedIssueKeys) =>
            getJiraBulkWorkItems({
              sonarIssueKeys: chunkedIssueKeys,
              sonarProjectId,
              resourceType,
            }),
          ),
        ).then((responses) => responses.flat());

        // Update the cache with the fetched work items
        filteredIssueKeys.forEach((sonarIssueKey) => {
          const workItem = result.find((item) => item.sonarIssueKey === sonarIssueKey);

          queryClient.setQueryData(
            jiraWorkItemQueryKeys.byKey(sonarProjectId, sonarIssueKey, resourceType),
            workItem ?? {},
          );
        });
        return [...result, ...cachedNotEmptyWorkItems];
      },
      enabled: isDefined(sonarProjectId) && Boolean(sonarIssueKeys.length),
      staleTime: StaleTime.NEVER,
    });
  },
);

export const useJiraProcessingWorkItemsQuery = createQueryHook(() => {
  return queryOptions({
    queryKey: ['jira-processing-work-items'],
    queryFn: getJiraProcessingWorkItems,
    staleTime: StaleTime.MEDIUM,
    refetchInterval: StaleTime.MEDIUM,
  });
});

export function usePatchJiraWorkTypesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patchJiraSelectedWorkTypes,
    onSuccess: (_, variables) => {
      // Silent invalidation and manual cache update
      queryClient.invalidateQueries({
        queryKey: [JIRA_WORK_TYPES, variables.sonarProjectId],
        refetchType: 'none',
      });
      queryClient.setQueriesData(
        { queryKey: [JIRA_WORK_TYPES, variables.sonarProjectId] },
        (oldData: JiraWorkType[]) => {
          if (!oldData) {
            return undefined;
          }
          return oldData.map((workType) => {
            return {
              ...workType,
              selected: variables.selectedWorkTypes.includes(workType.id),
            };
          });
        },
      );
    },
  });
}

export const useJiraUserActionsQuery = createQueryHook((sonarProjectId: string) => {
  return queryOptions({
    queryKey: [JIRA_USER_ACTIONS, sonarProjectId],
    queryFn: () => getJiraUserActions(sonarProjectId),
    enabled: isDefined(sonarProjectId),
    staleTime: StaleTime.LONG,
  });
});

export const useJiraLinkedIssuesCountQuery = createQueryHook((sonarProjectId: string) => {
  return queryOptions({
    queryKey: [JIRA_LINKED_ISSUES_COUNT, sonarProjectId],
    queryFn: () => getJiraLinkedIssuesCount(sonarProjectId),
    enabled: isDefined(sonarProjectId),
    staleTime: StaleTime.LIVE,
  });
});
